package com.trevorwiebe.timesheet.more.presentation

import com.trevorwiebe.timesheet.core.presentation.DisposableViewModel
import androidx.lifecycle.viewModelScope
import com.trevorwiebe.timesheet.core.data.FirestoreListenerRegistry
import com.trevorwiebe.timesheet.core.domain.CoreRepository
import com.trevorwiebe.timesheet.core.domain.model.DatabaseUserModel
import com.trevorwiebe.timesheet.core.domain.model.Punch
import com.trevorwiebe.timesheet.core.domain.model.PunchType
import com.trevorwiebe.timesheet.core.domain.model.Rate
import com.trevorwiebe.timesheet.punch.domain.PunchRepository
import com.trevorwiebe.timesheet.punch.domain.usecases.AddUpHours
import com.trevorwiebe.timesheet.punch.presentation.uiUtils.UiPunch
import kotlinx.coroutines.channels.Channel
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.receiveAsFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import kotlin.time.Clock
import kotlinx.datetime.LocalDateTime
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime
import kotlin.time.ExperimentalTime

class MoreViewModel(
    private val coreRepository: CoreRepository,
    private val punchRepository: PunchRepository,
    private val addUpHours: AddUpHours
) : DisposableViewModel() {

    private val _state = MutableStateFlow(MoreState())
    val state = _state.asStateFlow()

    private val _onSignOutChannel = Channel<Unit>()
    val onSignOutChannel = _onSignOutChannel.receiveAsFlow()

    init {
        getCurrentUser()
        getRates()
    }

    fun onEvent(event: MoreEvents) {
        when (event) {
            is MoreEvents.OnShowConfirmSignOutSheet -> {
                _state.update { it.copy(confirmSignOutSheet = event.show) }
            }

            is MoreEvents.OnSignOut -> {
                signOut()
            }

            is MoreEvents.OnRetryLoadUser -> {
                getCurrentUser()
            }
        }
    }

    private fun signOut() {
        _state.update { it.copy(signOutLoading = true) }
        viewModelScope.launch {
            FirestoreListenerRegistry.clearAllListeners()
            val result = coreRepository.signOut()
            if (result.error == null) {
                _state.update { it.copy(confirmSignOutSheet = false) }
                _onSignOutChannel.send(Unit)
            }
        }
    }

    private fun getCurrentUser() {
        viewModelScope.launch {
            _state.update { it.copy(userLoading = true, userLoadFailed = false) }

            val user = coreRepository.getDatabaseUser().data as? DatabaseUserModel
            if (user == null) {
                _state.update { it.copy(userLoading = false, userLoadFailed = true) }
                return@launch
            }
            _state.update {
                it.copy(currentUser = user, userLoading = false, userLoadFailed = false)
            }
        }
    }

    @Suppress("UNCHECKED_CAST")
    private fun getRates(){
        viewModelScope.launch {
            val result = punchRepository.getRates()
            if (result.error == null) {
                val rates = result.data as List<Rate>
                _state.update { it.copy(rates = rates) }

                getAllPunchesForAYear()
            }
        }
    }

    @OptIn(ExperimentalTime::class)
    @Suppress("UNCHECKED_CAST")
    private fun getAllPunchesForAYear(){

        val currentDate = Clock.System.now().toLocalDateTime(TimeZone.UTC)
        val year = currentDate.year

        val startOfYear = LocalDateTime(year, 1, 1, 0, 0)
        val endOfYear = LocalDateTime(year, 12, 31, 23, 59)

        val job = viewModelScope.launch {
            punchRepository.getPunches(startOfYear, endOfYear).collect {
                if (it.error == null) {
                    val punches = it.data as List<Punch>
                    val uiPunchList = processPunches(punches)
                    val hoursWorked = addUpHours(
                        uiPunchList.filter { it.punchInError == null && it.punchOutError == null },
                        _state.value.rates
                    )
                    _state.update { it.copy(hoursWorked = hoursWorked) }
                }
            }
        }
        FirestoreListenerRegistry.registerJob(job)
    }

    private fun processPunches(punches: List<Punch>): List<UiPunch>{
        val punchesByRateId: Map<String, List<Punch>> = punches
            .groupBy { it.rateId }
            .mapValues { (_, punchesForRate) ->
                punchesForRate.sortedBy { it.dateTime }
            }

        val punchUiList = mutableListOf<UiPunch>()

        punchesByRateId.forEach {
            val punchedIn = it.value.filter { it.type == PunchType.IN }
            val punchedOut = it.value.filter { it.type == PunchType.OUT }
            val uiPunchList = createUiPunchList(punchedIn, punchedOut)
            punchUiList.addAll(uiPunchList)
        }
        return punchUiList
    }

    private fun createUiPunchList(punchIn: List<Punch>, punchOut: List<Punch>): List<UiPunch> {
        val timeUiPunches = mutableListOf<UiPunch>()
        punchIn.forEachIndexed { index, punchIn ->
            val punchOut = punchOut.getOrNull(index)
            val punchOutError = if (punchOut != null && punchIn.dateTime > punchOut.dateTime) {
                "Clock out time must be after clock in time"
            } else null

            val uiPunch = UiPunch(punchIn, punchOut, null, punchOutError)
            timeUiPunches.add(uiPunch)
        }
        return timeUiPunches
    }
}